
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TrainingInput } from "./types";
import { Card, CardContent } from "@/components/ui/card";
import { Badge, Clock, DollarSign, Info, MapPin, Upload } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import languages from "@/data/languages.json";
import categoryNames from "@/data/categoryNames.json";

interface BasicInfoStepProps {
  newTraining: Partial<TrainingInput>;
  setNewTraining: (training: Partial<TrainingInput>) => void;
  setImageFile: (file: File | null) => void;
  imagePreview: string;
  setImagePreview: (preview: string) => void;
}

const BasicInfoStep: React.FC<BasicInfoStepProps> = ({
  newTraining,
  setNewTraining,
  setImageFile,
  imagePreview,
  setImagePreview,
}) => {
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Helper function to update nested objects
  const updateTraining = (key: string, value: any) => {
    setNewTraining({ ...newTraining, [key]: value });
  };

  return (
    <div className="space-y-6">
      {/* Header with guidance */}
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
        <div className="flex items-start gap-3">
          <Info className="text-blue-500 h-5 w-5 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-700">Course Basic Information</h3>
            <p className="text-sm text-blue-600">
              This information will be displayed prominently to users browsing available courses.
              Be descriptive and accurate to attract the right participants.
            </p>
          </div>
        </div>
      </div>

      {/* Title section - removed provider */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title" className="text-base">Course Title</Label>
              <Input
                id="title"
                value={newTraining.title || ""}
                onChange={(e) => updateTraining("title", e.target.value)}
                placeholder="e.g. Advanced React Development"
                className="h-10"
                required
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Course details section */}
      <Card>
        <CardContent className="pt-6">
          <h3 className="text-lg font-medium mb-4">Course Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-gray-500" />
                <Label htmlFor="price" className="text-base">Price</Label>
              </div>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-gray-500">$</span>
                <Input
                  id="price"
                  type="number"
                  value={newTraining.price || ""}
                  onChange={(e) => updateTraining("price", parseFloat(e.target.value) || 0)}
                  className="pl-8 h-10"
                  placeholder="0.00"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-gray-500" />
                <Label htmlFor="duration" className="text-base">Duration</Label>
              </div>
              <Input
                id="duration"
                value={newTraining.duration || ""}
                onChange={(e) => updateTraining("duration", e.target.value)}
                placeholder="e.g. 6 weeks"
                className="h-10"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="language" className="text-base">Language</Label>
              <Select
                value={newTraining.language || "en"}
                onValueChange={(value) => updateTraining("language", value)}
              >
                <SelectTrigger id="language" className="h-10">
                  <SelectValue placeholder="Select language" />
                </SelectTrigger>
                <SelectContent className="bg-white z-50">
                  {languages.languages.map((lang) => (
                    <SelectItem key={lang.code} value={lang.name}>
                      {lang.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-6 space-y-2">
            <Label htmlFor="description" className="text-base">Description</Label>
            <Textarea
              id="description"
              value={newTraining.description || ""}
              onChange={(e) => updateTraining("description", e.target.value)}
              placeholder="Provide a detailed description of your course..."
              className="min-h-32 resize-y"
              required
            />
          </div>
        </CardContent>
      </Card>

      {/* Course classification section */}
      <Card>
        <CardContent className="pt-6">
          <h3 className="text-lg font-medium mb-4">Course Classification</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label htmlFor="level" className="text-base">Difficulty Level</Label>
              <Select
                value={newTraining.level || "Beginner"}
                onValueChange={(value) => updateTraining("level", value)}
              >
                <SelectTrigger id="level" className="h-10">
                  <SelectValue placeholder="Select level" />
                </SelectTrigger>
                <SelectContent className="bg-white z-50">
                  <SelectItem value="Beginner">Beginner</SelectItem>
                  <SelectItem value="Intermediate">Intermediate</SelectItem>
                  <SelectItem value="Advanced">Advanced</SelectItem>
                  <SelectItem value="Expert">Expert</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="format" className="text-base">Course Format</Label>
              <Select
                value={newTraining.format || "Online"}
                onValueChange={(value) => updateTraining("format", value)}
              >
                <SelectTrigger id="format" className="h-10">
                  <SelectValue placeholder="Select format" />
                </SelectTrigger>
                <SelectContent className="bg-white z-50">
                  <SelectItem value="Online">Online</SelectItem>
                  <SelectItem value="In-person">In-person</SelectItem>
                  <SelectItem value="Hybrid">Hybrid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="category" className="text-base">Category</Label>
              <Select
                value={newTraining.category || "Programming"}
                onValueChange={(value) => updateTraining("category", value)}
              >
                <SelectTrigger id="category" className="h-10">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="bg-white z-50">
                  {categoryNames.categories.map((name) => (
                    <SelectItem key={name} value={name}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Conditional Location Field */}
          {(newTraining.format === "In-person" || newTraining.format === "Hybrid") && (
            <div className="mt-6 space-y-2 border-t pt-6">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-gray-500" />
                <Label htmlFor="location" className="text-base">Location</Label>
              </div>
              <Input
                id="location"
                value={newTraining.location || ""}
                onChange={(e) => updateTraining("location", e.target.value)}
                placeholder="Enter physical location"
                className="h-10"
                required
              />
              <p className="text-sm text-gray-500">Required for in-person or hybrid courses</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Certificate section */}
      <Card>
        <CardContent className="py-6">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <h3 className="text-base font-medium">Certificate Upon Completion</h3>
              <p className="text-sm text-gray-500">Will participants receive a certificate?</p>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="certificate"
                checked={newTraining.certificate || false}
                onCheckedChange={(checked) => updateTraining("certificate", checked)}
              />
              <Label htmlFor="certificate" className="cursor-pointer">
                {newTraining.certificate ? "Yes" : "No"}
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Image upload section - FIXED */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-1 space-y-2">
              <Label htmlFor="image" className="text-base">Course Image</Label>
              <label htmlFor="image-upload" className="block">
                <div className="border-2 border-dashed rounded-lg p-6 text-center hover:bg-gray-50 transition-colors cursor-pointer">
                  <div className="flex flex-col items-center justify-center">
                    <Upload className="h-8 w-8 text-gray-400 mb-2" />
                    <p className="text-sm font-medium">Click or drag to upload an image</p>
                    <p className="text-xs text-gray-500 mt-1">SVG, PNG, JPG or GIF (max. 2MB)</p>
                  </div>
                </div>
                <Input
                  type="file"
                  id="image-upload"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                  required
                />
              </label>
            </div>

            {imagePreview ? (
              <div className="flex flex-col items-center md:w-1/3">
                <div className="rounded-md overflow-hidden border w-full h-48 mb-2">
                  <img
                    src={imagePreview}
                    alt="Course preview"
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="mt-2 px-2 py-1 text-sm border rounded-md">Preview</div>
              </div>
            ) : (
              <div className="md:w-1/3 flex items-center justify-center">
                <div className="text-center p-6 bg-gray-50 rounded-lg border border-dashed w-full h-48 flex flex-col items-center justify-center">
                  <p className="text-gray-400 text-sm">No image selected</p>
                  <p className="text-xs text-gray-400 mt-1">Preview will appear here</p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BasicInfoStep;